import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import AppBar from '@material-ui/core/AppBar';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';
import TicketsTable from '../TicketsTable/TicketsTable';
import ApiService from '../../services/ApiService';
import helpers from '../AddAnomaly/FileUploader/Helpers';
import {
  setTickets,
  ticketsForWorkshopTimeState,
  rowsFiltersForWorkshopTimeState,
  initializeRowsFiltersForWorkshopTime,
  setSortConfigForWorkshopTime,
  sortConfigForWorkshopTimeState,
} from './WorkshopTimeSlice';

import useStyles from './styles';

const FileSaver = require('file-saver');

const headCells = [
  { id: 'ticketNumber', numeric: false, label: 'Ticket ID' },
  { id: 'wagonNumber', numeric: false, label: 'Wagon' },
  { id: 'nextRevisionDate', numeric: false, label: 'WS scheduled date' },
  { id: 'wsStartDate', numeric: false, label: 'WS start date' },
  { id: 'wsEndDate', numeric: false, label: 'WS end date' },
  { id: 'wsDuration', numeric: false, label: 'WS duration (days)' },
  {
    id: 'wsContractualDuration',
    numeric: false,
    label: 'WS duration in contract (days)',
  },
  { id: 'overdueTime', numeric: true, label: 'Overdue time (days)' },
  {
    id: 'ticketStatus',
    numeric: false,
    label: 'Ticket status',
  },
  { id: 'creditNotes', numeric: false, label: 'Credit notes' },
  { id: 'contractNumber', numeric: false, label: 'Contract number' },
  { id: 'keeper', numeric: false, label: 'Keeper' },
  { id: 'hasAlert', numeric: false, label: 'Alert' },
];

export default function WorkshopTimePage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const plannedTickets = useSelector(ticketsForWorkshopTimeState);
  const [rowsFiltered, setRowsFiltered] = React.useState([]);
  const rowsFilters = useSelector(rowsFiltersForWorkshopTimeState);
  const sortConfig = useSelector(sortConfigForWorkshopTimeState);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [hasFilters, setHasFilters] = React.useState(false);

  useEffect(() => {
    if (plannedTickets.length === 0) {
      setLoading(true);
    }
    ApiService.findAllPlannedMaintenancesWentToWorkshop()
      .then((response) => {
        setLoading(false);
        dispatch(setTickets(response.data));
      })
      .catch(() => {
        setLoading(false);
        console.log('error while loading allPlannedMaintWenToWorkshop');
      });
  }, []);

  useEffect(() => {
    setRowsFiltered(plannedTickets);
  }, [plannedTickets]);

  useEffect(() => {
    let result = plannedTickets;
    rowsFilters.map((item) => {
      if (item.selectedOptions.length > 0) {
        result = result.filter(
          (row) => item.selectedOptions.includes(t(row[item.cell])) === true,
        );
      }
      return true;
    });
    setPage(0);
    setRowsFiltered(result);
  }, [plannedTickets, rowsFilters]);

  useEffect(() => {
    if (rowsFilters.length > 0 || sortConfig.length > 0) {
      setHasFilters(true);
    }
  }, [rowsFilters, sortConfig]);

  function downloadToExcel() {
    setLoading(true);
    ApiService.downloadWorkshopTimeToExcel()
      .then((response) => {
        const blob = new Blob([helpers.base64toBlob(response.data)], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        FileSaver.saveAs(blob, 'Planned Maintenance Contractual Commitment');
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  function clearAllFilters() {
    dispatch(initializeRowsFiltersForWorkshopTime());
    dispatch(setSortConfigForWorkshopTime([]));
    setHasFilters(false);
  }

  function getRowsPerPage(newRowsPerPage) {
    setRowsPerPage(newRowsPerPage);
  }

  function getPage(newPage) {
    setPage(newPage);
  }

  return (
    <div className={classes.root}>
      {loading && (
        <Paper
          data-testid="loader"
          elevation={0}
          position="absolute"
          zindex="2"
        >
          <CircularProgress className={classes.loader} />
        </Paper>
      )}
      <AppBar elevation={0} position="static" className={classes.appBar}>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <ButtonGroup className={classes.buttonGroup} variant="contained">
              <Button className={classes.selectedTab}>
                {t('Overdue workshop time')}
              </Button>
            </ButtonGroup>
          </Grid>
          <Grid item xs={4}>
            <Grid container justify="flex-end" alignItems="flex-end">
              <Button
                size="small"
                data-testid="download"
                disableRipple
                className={classes.button}
                color="secondary"
                onClick={downloadToExcel}
              >
                <Typography
                  className={
                    plannedTickets.length > 0
                      ? classes.selectedButton
                      : classes.endBarButton
                  }
                >
                  {t('Export')}
                </Typography>
              </Button>
              <Button
                data-testid="clearAllFilters"
                size="small"
                disableRipple
                className={classes.button}
                color="secondary"
                onClick={clearAllFilters}
              >
                <Typography
                  className={
                    hasFilters ? classes.selectedButton : classes.endBarButton
                  }
                >
                  {t('clear all filters')}
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </AppBar>
      <Paper elevation={0} className={classes.contentPaper}>
        <div className={classes.description}>
          {t('contractual commitment description')}
        </div>
      </Paper>
      <TicketsTable
        headCells={headCells}
        ticketList={plannedTickets}
        rows={rowsFiltered}
        count={rowsFiltered.length}
        rowsPerPage={rowsPerPage}
        page={page}
        setRowsPerPage={getRowsPerPage}
        setPage={getPage}
      />
    </div>
  );
}
